<template>
  <div class="main-header d-print-none">
    <div class="container-fluid">
      <div class="row">
        <div class="col d-flex flex-row align-items-center">
          <router-link v-if="$appState && $appState.context && logo" v-close-menu class="logo" :style="logo" :to="{ name: 'operative-company.dashboard.redirect' }" />
          <router-link v-else-if="$appState && $appState.context" v-close-menu class="logo-initials" :to="{ name: 'operative-company.dashboard.redirect' }">
            <BlockWithInitials :name="companyName" />
          </router-link>

          <h1 class="company-name mb-0 text-primary h4">{{ companyName }}</h1>
        </div>
        <div class="col-auto d-none d-md-flex align-items-center">
          <template v-if="forecast && forecast.id">
            <p class="mb-0">
              <span class="font-weight-light mr-3">
                Visualizzando il previsionale "<strong>{{forecast.name}}</strong>"
              </span>
              <button class="btn btn-danger col-auto" @click="removeForecast()">
                <i class="fas fa-times"></i>
              </button>
            </p>
            <div class="divider vertical mx-3"></div>
          </template>

          <AccountDropdown class="col-auto btn btn-primary" @action-selected="actionSelected" :active="activeAction" />
        </div>
        <Hamburger class="col-auto d-flex align-items-center d-md-none px-4"></Hamburger>
      </div>
    </div>

    <BModal title="Profilo" dialog-class="modal--type-dialog" id="profile-modal" ref="profile-modal" hide-footer centered scrollable>
      <AccountForm :edit="true" :user="user" />
    </BModal>

    <BModal title="Cambia Password" dialog-class="modal--type-dialog" id="change-password-modal" ref="change-password-modal" hide-footer centered scrollable>
      <ProfilePasswordChangeForm />
    </BModal>

    <BModal dialog-class="modal--type-dialog" id="error-modal" ref="error-modal" centered scrollable>
      Si è verificato un errore durante il recupero dei dati dell'utente, si prega di riprovare
      <template #modal-footer>
        <button class="ml-2 btn btn-outline-danger" @click="close('error-modal')">Chiudi</button>
      </template>
    </BModal>
  </div>
</template>

<script>

import { BModal } from 'bootstrap-vue';
import closeMenuDirective from '../../../../libs/Sidebar/directives/closeMenu';

export default {
  name: 'layout-header',
  directives: {
    closeMenu: closeMenuDirective,
  },
  components: {
    BModal,
    AccountForm: () => import('../../Account/AccountForm'),
    ProfilePasswordChangeForm: () => import('../../Account/ProfilePasswordChangeForm'),
    AccountDropdown: () => import('../../Account/AccountDropdown'),
    Hamburger: () => import('../../../../libs/Sidebar/components/Hamburger/CssHamburger'),
    BlockWithInitials: () => import('../../Ui/BlockWithInitials'),
  },
  data () {
    return {
      user: null,
      activeAction: null,
    };
  },
  computed: {
    companyName () {
      return this.$appState?.context?.name;
    },
    showContextSelect () {
      return this.$oauth2.me && (this.$oauth2.me.companies?.length || 0) > 1;
    },
    home () {
      if (!this.$oauth2.me) {
        return { name: 'login' };
      }

      if (this.$oauth2.me.admin || this.$oauth2.me.consultant) {
        return { name: 'management' };
      } else {
        return { name: 'operative-company', params: { id: this.$appState.context?.id } };
      }
    },
    logo () {
      return !this.$appState?.context?.logo ? null : {
        'background-image': `url(${this.$appState.context.logo})`,
      };
    },
    forecast () {
      return this.$appState.forecast;
    },
  },
  methods: {
    logout () {
      this.removeForecast({ to: false });
      this.$router.push({ name: 'logout' });
    },
    async actionSelected (name) {
      if (name === 'logout') {
        this.activeAction = 'logout';
        this.removeForecast({ to: false });

        await this.$router.push({ name: 'logout' });

        return;
      }

      if (name === 'profile') {
        this.activeAction = 'profile';

        try {
          const res = await this.$api.getMyProfile();
          this.user = res.data;
          this.$refs['profile-modal'].show();
        } catch (error) {
          this.$refs['error-modal'].show();
        }

        return;
      }

      if (name === 'changePassword') {
        this.activeAction = 'changePassword';
        this.$refs['change-password-modal'].show();
      }
    },
    removeForecast (options = { to: { name: 'operative-company.dashboard.redirect' } }) {
      if (!this.$appState.forecast) {
        return;
      }

      this.$appState.forecast = null;

      if (options?.to) {
        this.$router.replace(options.to);
      }
    },
  },
  watch: {
    $route: {
      handler: function (newValue) {
        if (!this.$appState?.context?.id) {
          this.$appState.forecast = null;

          return;
        }

        const urlForecastId = newValue.query.forecast;
        const appStateForecast = this.$appState.forecast;

        if (urlForecastId) {
          if (appStateForecast && appStateForecast?.id && urlForecastId === appStateForecast.id) {
            return;
          }

          this.$api.company(this.$appState.context.id)
            .then(res => {
              this.$appState.context = res.data || null;
              this.$appState.forecast = res?.data?.forecast || null;
              if (urlForecastId && this.$appState.forecast === null) {
                this.$router.replace({ name: 'operative-company', params: { id: this.$appState.context.id } });
              }
            })
            .catch(() => {
              this.$appState.context = null;
              this.$appState.forecast = null;
              this.$router.replace({ name: 'root' });
            })
          ;

          return;
        }

        if (appStateForecast) {
          this.$appState.forecast = null;
        }
      },
      immediate: true,
    },
  },
};

</script>

<style lang="scss">

$main-header-background-color: #999 !default;
$main-header-text-color: #fff !default;
$main-header-border-bottom-color: #777 !default;
$main-header-box-shadow-color: #2c2c2c !default;
$main-header-height-mobile: 60px !default;
$main-header-height-tablet: 87px !default;
$main-header-height-desktop: $main-header-height-tablet !default;

.operative-layout {
  .main-header {
    flex-shrink: 0;
    background-color: $main-header-background-color;
    box-shadow: 0px 3px 6px rgba($main-header-box-shadow-color, .11);
    color: $main-header-text-color;
    font-weight: bold;
    height: $main-header-height-mobile;
    padding: 5px 0;
    z-index: 999;

    @include media-breakpoint-up("md") {
      height: $main-header-height-tablet;
      padding: 8px 0;
    }

    @include media-breakpoint-up("lg") {
      height: $main-header-height-desktop;
    }

    &>.container {
      padding-right: 0;

      @include media-breakpoint-up("md") {
        padding-right: 15px;
      }
    }

    .logo {
      display: block;
      width: 90px;
      height: 50px;
      background-image: none;

      @include media-breakpoint-up("md") {
        width: 130px;
        height: 70px;
      }
    }

    .logo-initials {
      width: 44px;
      height: 44px;
      margin-right: 15px;
      border-radius: 10px;
      position: relative;
      background: #B5DDF2;

      &:hover {
        text-decoration: none;
      }

      span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 22px;
        line-height: 26px;
        color: white !important;
      }
    }

    .company-name {
      font-size: 24px;
      line-height: 28px;
      font-weight: bold;
    }
  }

  .divider {
    background: #e3e3e3;

    &.vertical {
      height: 100%;
      width: 1px;
    }
  }
}

.modal-header {
  .close {
    background: none;
    border: none;
    box-shadow: none;
  }
}

.modal-backdrop {
  opacity: 0.5;
}

</style>
